'use client';

import type {ReactNode} from 'react';
import type {DetailPageAnalytics} from '../../analytics';
import {mapShareUrl, type MapCoordinates} from '../map';
import {PropertySummary} from '../property-summary';
import {openRegisterInterest} from '../register-interest/register-interest-controller';

export interface DetailSummaryProps {
  item: {
    uuid: string;
    name: string;
    location: string;
    logoUrl?: string;
    tourUrl?: string;
    linkLocation?: string;
    nearbyCoordinates?: MapCoordinates;
    startingPrice?: number;
  };
  analytics: DetailPageAnalytics;
  registerInterestProjectId: string;
  registerInterestPhaseId?: string;
  registerInterestDesignTypeId?: string;
  eyebrow?: ReactNode;
}

export function DetailSummary({
  item,
  analytics,
  registerInterestProjectId,
  registerInterestPhaseId,
  registerInterestDesignTypeId,
  eyebrow,
}: DetailSummaryProps) {
  const tracked = {id: item.uuid, name: item.name};
  const locationHref = item.linkLocation ?? (item.nearbyCoordinates ? mapShareUrl(item.nearbyCoordinates) : undefined);
  return (
    <PropertySummary
      // Figma node 547-30949 is 156 tall around a 100px content container, i.e.
      // 28px block padding. The breakpoint key must be spelled out: a flat `py`
      // loses to Section's own `up('md')`-scoped default at desktop widths.
      sx={{py: {xs: 2.75, md: 3.5}}}
      name={item.name}
      location={item.location}
      locationHref={locationHref}
      onLocationClick={() => {
        analytics.map(tracked);
      }}
      logoSrc={item.logoUrl}
      eyebrow={eyebrow}
      startingPrice={item.startingPrice}
      onRegisterInterest={() => {
        analytics.registerInterest(tracked);
        openRegisterInterest({
          projectId: registerInterestProjectId,
          phaseId: registerInterestPhaseId,
          designTypeId: registerInterestDesignTypeId,
        });
      }}
      tour={
        item.tourUrl
          ? {
              url: item.tourUrl,
              onClick: () => {
                analytics.takeTour(tracked);
              },
            }
          : undefined
      }
    />
  );
}

DetailSummary.displayName = 'DetailSummary';
